import React, { useState, useEffect, useCallback } from 'react';
import InputField from '../../../../components/InputField/InputField';
import Toggle from '../../../../components/Toggle/Toggle';
import Button from '../../../../components/Button/Button';
import { useNoShowPolicy } from '../../../../hooks/useNoShowPolicy';
import { useToast } from '../../../../contexts/ToastContext';
import type { NoShowPolicyDto } from '../../../../services/api';
import styles from './NoShowPolicyPage.module.css';

const NoShowPolicyPage: React.FC = () => {
    const [policy, setPolicy] = useState<NoShowPolicyDto>({
        depositRequired: false,
        depositPercent: 0,
        gracePeriodMinutes: 15,
        cancellationWindowHours: 24
    });

    const { loading, error, getNoShowPolicy, updateNoShowPolicy } = useNoShowPolicy();
    const { showToast } = useToast();

    const fetchPolicy = useCallback(async () => {
        try {
            const data = await getNoShowPolicy();
            setPolicy(data);
        } catch {
            // error handled by hook
        }
    }, [getNoShowPolicy]);

    useEffect(() => {
        fetchPolicy();
    }, [fetchPolicy]);

    const handleSave = async () => {
        try {
            await updateNoShowPolicy(policy);
            showToast('No-show policy updated successfully.', 'success');
        } catch {
            showToast('Failed to update no-show policy.', 'error');
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>No-Show Policy</h1>
                <p className={styles.subtitle}>Configure how no-shows and late cancellations are handled</p>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    {error}
                    <Button label="Retry" onClick={fetchPolicy} size="small" />
                </div>
            )}

            <div className={styles.form}>
                <div className={styles.toggleRow}>
                    <div>
                        <div className={styles.toggleLabel}>Require Deposit</div>
                        <div className={styles.toggleDescription}>Require customers to pay a deposit when booking</div>
                    </div>
                    <Toggle
                        checked={policy.depositRequired}
                        onChange={(val) => setPolicy(prev => ({ ...prev, depositRequired: val }))}
                    />
                </div>

                {policy.depositRequired && (
                    <div className={styles.field}>
                        <InputField
                            label="Deposit Percentage (%)"
                            value={String(policy.depositPercent)}
                            onChange={(val) => setPolicy(prev => ({ ...prev, depositPercent: Number(val) || 0 }))}
                            type="number"
                            placeholder="e.g. 20"
                        />
                    </div>
                )}

                <div className={styles.field}>
                    <InputField
                        label="Grace Period (minutes)"
                        value={String(policy.gracePeriodMinutes)}
                        onChange={(val) => setPolicy(prev => ({ ...prev, gracePeriodMinutes: Number(val) || 0 }))}
                        type="number"
                        placeholder="e.g. 15"
                    />
                </div>

                <div className={styles.field}>
                    <InputField
                        label="Cancellation Window (hours)"
                        value={String(policy.cancellationWindowHours)}
                        onChange={(val) => setPolicy(prev => ({ ...prev, cancellationWindowHours: Number(val) || 0 }))}
                        type="number"
                        placeholder="e.g. 24"
                    />
                </div>

                <div className={styles.actions}>
                    <Button label="Save Policy" onClick={handleSave} disabled={loading} />
                </div>
            </div>
        </div>
    );
};

export default NoShowPolicyPage;
